import {
  LayoutType,
  type MindmapElementModel,
  type MindmapNode,
  type MindmapRoot,
} from '@blocksuite/affine-model';
import type { SerializedXYWH } from '@blocksuite/global/gfx';
import { Bound } from '@blocksuite/global/gfx';

export const NODE_VERTICAL_SPACING = 45;
export const NODE_HORIZONTAL_SPACING = 110;
export const NODE_FIRST_LEVEL_HORIZONTAL_SPACING = 200;
// Vertical (TOP) layout spacing. The parent->child vertical gap must exceed the
// connector's minimum curve control length (~100px, see connector-manager
// `_generateCurveConnectorPath`) or the curves overshoot and bow upward. The
// first level is given extra room so the many branches leaving the single root
// point fan out and separate cleanly instead of overlapping.
export const NODE_FIRST_LEVEL_VERTICAL_SPACING = 160;
export const NODE_CHILD_VERTICAL_SPACING = 120;
// Horizontal gap between sibling subtrees in the TOP layout. Wider than the
// left/right layouts' node gap so sibling branches don't crowd/cross.
export const NODE_TOP_SIBLING_HORIZONTAL_SPACING = 130;

type TreeSize = {
  /**
   * The parent of the tree
   */
  parent: TreeSize | null;

  /**
   * The root node of the tree
   */
  root: MindmapNode;

  /**
   * The size of the tree, including its descendants
   */
  bound: Bound;

  /**
   * The size of the children of the root
   */
  children: TreeSize[];
};

const calculateNodeSize = (
  root: MindmapNode,
  parent: TreeSize | null = null,
  rootChildren?: MindmapNode[]
): TreeSize => {
  const bound = root.element.elementBound;
  const children: TreeSize[] = [];
  const firstLevel = parent === null;

  rootChildren = rootChildren ?? root.children;

  const treeSize: TreeSize = {
    parent,
    root,
    bound,
    children,
  };

  if (rootChildren?.length && !root.detail.collapsed) {
    const childrenBound = rootChildren.reduce(
      (pre, node) => {
        const childSize = calculateNodeSize(node, treeSize);

        children.push(childSize);

        pre.w = Math.max(pre.w, childSize.bound.w);
        pre.h +=
          pre.h > 0
            ? NODE_VERTICAL_SPACING + childSize.bound.h
            : childSize.bound.h;

        return pre;
      },
      new Bound(0, 0, 0, 0)
    );

    bound.w +=
      childrenBound.w +
      (firstLevel
        ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING
        : NODE_HORIZONTAL_SPACING);
    bound.h = Math.max(bound.h, childrenBound.h);
  }

  return treeSize;
};

const layoutTree = (
  tree: TreeSize,
  layoutType: LayoutType.LEFT | LayoutType.RIGHT,
  mindmap: MindmapElementModel,
  path: number[] = [0]
) => {
  const firstLevel = path.length === 1;
  const treeHeight = tree.bound.h;
  const currentX =
    layoutType === LayoutType.RIGHT
      ? tree.root.element.x +
        tree.root.element.w +
        (firstLevel
          ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING
          : NODE_HORIZONTAL_SPACING)
      : tree.root.element.x -
        (firstLevel
          ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING
          : NODE_HORIZONTAL_SPACING);
  let currentY = tree.root.element.y + (tree.root.element.h - treeHeight) / 2;

  if (tree.root.element.h >= treeHeight && tree.children.length) {
    const onlyChild = tree.children[0];

    currentY += (tree.root.element.h - onlyChild.root.element.h) / 2;
  }

  if (!tree.root.detail.collapsed) {
    tree.children.forEach((subtree, idx) => {
      const subtreeRootEl = subtree.root.element;
      const subtreeHeight = subtree.bound.h;
      const xywh = `[${
        layoutType === LayoutType.RIGHT ? currentX : currentX - subtreeRootEl.w
      },${currentY + (subtreeHeight - subtreeRootEl.h) / 2},${subtreeRootEl.w},${subtreeRootEl.h}]` as SerializedXYWH;

      const currentNodePath = [...path, idx];

      if (subtreeRootEl.xywh !== xywh) {
        subtreeRootEl.xywh = xywh;
      }

      layoutTree(subtree, layoutType, mindmap, currentNodePath);

      currentY += subtreeHeight + NODE_VERTICAL_SPACING;
    });
  }
};

const layoutRight = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSize(root, null);

  layoutTree(rootTree, LayoutType.RIGHT, mindmap, path);
};

const layoutLeft = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSize(root, null);

  layoutTree(rootTree, LayoutType.LEFT, mindmap, path);
};

const calculateNodeSizeVertical = (
  root: MindmapNode,
  parent: TreeSize | null = null,
  rootChildren?: MindmapNode[]
): TreeSize => {
  const bound = root.element.elementBound;
  const children: TreeSize[] = [];
  const firstLevel = parent === null;

  const treeSize: TreeSize = {
    parent,
    root,
    bound,
    children,
  };

  // `rootChildren` lets the balanced layout size only one half of the tree
  // (the nodes placed above the root, or the ones placed below it).
  const nodeChildren = firstLevel
    ? (rootChildren ?? root.children)
    : root.children;

  if (nodeChildren?.length && !root.detail.collapsed) {
    const childrenBound = nodeChildren.reduce(
      (pre, node) => {
        const childSize = calculateNodeSizeVertical(node, treeSize);

        children.push(childSize);

        pre.h = Math.max(pre.h, childSize.bound.h);
        pre.w +=
          pre.w > 0
            ? NODE_TOP_SIBLING_HORIZONTAL_SPACING + childSize.bound.w
            : childSize.bound.w;

        return pre;
      },
      new Bound(0, 0, 0, 0)
    );

    bound.h +=
      childrenBound.h +
      (firstLevel
        ? NODE_FIRST_LEVEL_VERTICAL_SPACING
        : NODE_CHILD_VERTICAL_SPACING);
    bound.w = Math.max(bound.w, childrenBound.w);
  }

  return treeSize;
};

/**
 * @param dir  1 places children below the parent (TOP layout),
 *            -1 places them above it (BOTTOM layout).
 */
const layoutTreeVertical = (
  tree: TreeSize,
  mindmap: MindmapElementModel,
  dir: 1 | -1,
  path: number[] = [0]
) => {
  const firstLevel = path.length === 1;
  const treeWidth = tree.bound.w;
  const rootEl = tree.root.element;
  const gap = firstLevel
    ? NODE_FIRST_LEVEL_VERTICAL_SPACING
    : NODE_CHILD_VERTICAL_SPACING;

  let currentX = rootEl.x + (rootEl.w - treeWidth) / 2;

  if (rootEl.w >= treeWidth && tree.children.length) {
    const onlyChild = tree.children[0];

    currentX += (rootEl.w - onlyChild.root.element.w) / 2;
  }

  if (!tree.root.detail.collapsed) {
    tree.children.forEach((subtree, idx) => {
      const subtreeRootEl = subtree.root.element;
      const subtreeWidth = subtree.bound.w;

      // Downward: the child's top edge sits `gap` below the parent's bottom.
      // Upward: its bottom edge sits `gap` above the parent's top, so the
      // child's own height has to be subtracted as well.
      const currentY =
        dir === 1
          ? rootEl.y + rootEl.h + gap
          : rootEl.y - gap - subtreeRootEl.h;

      const xywh = `[${currentX + (subtreeWidth - subtreeRootEl.w) / 2},${currentY},${subtreeRootEl.w},${subtreeRootEl.h}]` as SerializedXYWH;

      const currentNodePath = [...path, idx];

      if (subtreeRootEl.xywh !== xywh) {
        subtreeRootEl.xywh = xywh;
      }

      layoutTreeVertical(subtree, mindmap, dir, currentNodePath);

      currentX += subtreeWidth + NODE_TOP_SIBLING_HORIZONTAL_SPACING;
    });
  }
};

const layoutTop = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSizeVertical(root, null);

  layoutTreeVertical(rootTree, mindmap, 1, path);
};

const layoutBottom = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSizeVertical(root, null);

  layoutTreeVertical(rootTree, mindmap, -1, path);
};

/**
 * Vertical counterpart of `layoutBalance`: half the first-level branches are
 * placed below the root and half above it. It reuses the root's `right`/`left`
 * buckets (the same ones the horizontal balanced layout fills) — `right` is the
 * downward half, `left` the upward half.
 */
const layoutBalanceVertical = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSizeVertical(root, null);
  // A collapsed side lays out as if it had no branches at all — they are hidden
  // anyway, and leaving them in would keep their space reserved.
  const upTree: MindmapNode[] = mindmap.isSideCollapsed('left')
    ? []
    : (root as MindmapRoot).left;
  const downTree: MindmapNode[] = mindmap.isSideCollapsed('right')
    ? []
    : (root as MindmapRoot).right;

  {
    const downSize = calculateNodeSizeVertical(root, null, downTree);
    const mockRoot = {
      parent: null,
      root: rootTree.root,
      bound: downSize.bound,
      children: downSize.children,
    };

    layoutTreeVertical(mockRoot, mindmap, 1, path);
  }

  {
    const upSize = calculateNodeSizeVertical(root, null, upTree);
    const mockRoot = {
      parent: null,
      root: rootTree.root,
      bound: upSize.bound,
      children: upSize.children,
    };

    layoutTreeVertical(mockRoot, mindmap, -1, [0]);
  }
};

const layoutBalance = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSize(root, null);
  // A collapsed side lays out as if it had no branches at all — they are hidden
  // anyway, and leaving them in would keep their space reserved.
  const leftTree: MindmapNode[] = mindmap.isSideCollapsed('left')
    ? []
    : (root as MindmapRoot).left;
  const rightTree: MindmapNode[] = mindmap.isSideCollapsed('right')
    ? []
    : (root as MindmapRoot).right;

  {
    const leftTreeSize = calculateNodeSize(root, null, leftTree);
    const mockRoot = {
      parent: null,
      root: rootTree.root,
      bound: leftTreeSize.bound,
      children: leftTreeSize.children,
    };

    layoutTree(mockRoot, LayoutType.LEFT, mindmap, path);
  }

  {
    const rightTreeSize = calculateNodeSize(root, null, rightTree);
    const mockRoot = {
      parent: null,
      root: rootTree.root,
      bound: rightTreeSize.bound,
      children: rightTreeSize.children,
    };

    layoutTree(mockRoot, LayoutType.RIGHT, mindmap, [0]);
  }
};

export const layout = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  layoutDir: LayoutType | null,
  path: number[]
) => {
  layoutDir = layoutDir ?? mindmap.layoutType;

  switch (layoutDir) {
    case LayoutType.RIGHT:
      return layoutRight(root, mindmap, path);
    case LayoutType.LEFT:
      return layoutLeft(root, mindmap, path);
    case LayoutType.BALANCE:
      return layoutBalance(root, mindmap, path);
    case LayoutType.TOP:
      return layoutTop(root, mindmap, path);
    case LayoutType.BOTTOM:
      return layoutBottom(root, mindmap, path);
    case LayoutType.BALANCE_VERTICAL:
      return layoutBalanceVertical(root, mindmap, path);
  }
};
